import React, {useEffect, useRef, useState} from 'react';
import {Link as RouterLink, useNavigate} from 'react-router-dom'
import {useCookies} from "react-cookie";
import {useTranslation} from "react-i18next";
import {
    Box, Flex, HStack, Image, Link, Button, Menu, MenuButton, MenuList, MenuItem,
    Avatar, Text, VStack, Container,
    IconButton, Drawer, DrawerBody, DrawerHeader, DrawerOverlay, DrawerContent, DrawerCloseButton,
    Accordion, AccordionItem, AccordionButton, AccordionPanel, AccordionIcon,
    Divider, useDisclosure
} from '@chakra-ui/react'
import { HamburgerIcon } from '@chakra-ui/icons'
import logoImage from '../../media/Logo.png';
import logoutIcon from '../../media/Logout.svg';
import userAvatar from '../../media/User_default_avatar.png';
import {useFetchUserData, useLogout} from "../publicMethods/publicFunctionMethods";

const languageDisplayMapping: { [key: string]: string } = {
    "en": 'English',
    "fr": 'Français',
    "zh": "中文",
    "ar": "العربية",
    "ja": "日本語",
    "zh-CHS": '简体中文',
    "zh-CHZ": '繁体中文'
}

const availableLanguages = [
    { value: 'en', label: languageDisplayMapping.en },
    { value: 'fr', label: languageDisplayMapping.fr },
    { value: 'zh-CHS', label: languageDisplayMapping['zh-CHS'] },
    { value: 'ar', label: languageDisplayMapping.ar },
];

const AnimatedArrowIcon = ({ isOpen }: { isOpen: boolean }) => (
    <Box
        as="span"
        display="inline-flex"
        alignItems="center"
        justifyContent="center"
        transition="transform 0.3s ease"
        transform={isOpen ? "rotate(180deg)" : "rotate(0deg)"}
    >
        <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 20 20"
            width="16px"
            height="16px"
            fill="none"
        >
            <path
                d="M14.128 7.16482C14.3126 6.95983 14.6298 6.94336 14.835 7.12771C15.0402 7.31242 15.0567 7.62952 14.8721 7.83477L10.372 12.835L10.2939 12.9053C10.2093 12.9667 10.1063 13 9.99995 13C9.85833 12.9999 9.72264 12.9402 9.62788 12.835L5.12778 7.83477L5.0682 7.75273C4.95072 7.55225 4.98544 7.28926 5.16489 7.12771C5.34445 6.96617 5.60969 6.95939 5.79674 7.09744L5.87193 7.16482L9.99995 11.7519L14.128 7.16482Z"
                fill="currentColor"
            />
        </svg>
    </Box>
);

const Header: React.FC = ({...props}) => {
    const navigate = useNavigate();

    const [isObjectiveOpen, setIsObjectiveOpen] = useState(false);
    const [isSubjectiveOpen, setIsSubjectiveOpen] = useState(false);
    const objectiveCloseTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
    const subjectiveCloseTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

    const [cookies, setCookie, removeCookie] = useCookies(
        ['Authorization', 'firstName', 'lastName', 'avatar']
    );

    const { i18n } = useTranslation();

    const { t } = useTranslation();

    const isLogin = (cookies.Authorization && cookies.Authorization!=='undefined');

    const fetchUserData = useFetchUserData();

    const { logout } = useLogout();

    const [displayLanguage, setDisplayLanguage] = useState(languageDisplayMapping[i18n.language]);
    const [userData, setUserData] = useState({
        user_id: undefined,
        email: undefined,
        avatar: undefined,
        firstname: undefined,
        lastname: undefined,
        language: undefined,
        phone: undefined,
        email_verified: undefined,
        plan_expire: undefined,
        plan_level: undefined,
        region: undefined
    });

    const changeLanguage = (lng: string) => {
        i18n.changeLanguage(lng);
        localStorage.setItem('userLanguage', lng);
        setDisplayLanguage(languageDisplayMapping[lng])

    };

    const handleNavigation = (path: string) => {
        navigate(path);
        window.location.reload();
    }

    useEffect(() => {
        const savedLanguage = localStorage.getItem('userLanguage');
        if (savedLanguage) {
            i18n.changeLanguage(savedLanguage);
            setDisplayLanguage(languageDisplayMapping[savedLanguage] || languageDisplayMapping.en);
        }
    }, [i18n]);

    useEffect(() => {
        setDisplayLanguage(languageDisplayMapping[i18n.language] || languageDisplayMapping.en);
    }, [i18n.language]);

    useEffect(() => {
        if (cookies.firstName === undefined || cookies.firstName === null) {
            fetchUserData(setUserData);
        }
    }, [cookies.Authorization, cookies.firstName]);

    const { isOpen: isDrawerOpen, onOpen: openDrawer, onClose: closeDrawer } = useDisclosure();
    const isRTL = i18n.dir() === 'rtl';
    const drawerPlacement = isRTL ? 'left' : 'right';

    const handleDrawerNav = (path: string) => {
        closeDrawer();
        handleNavigation(path);
    };

    const handleDrawerLanguage = (lang: string) => {
        closeDrawer();
        changeLanguage(lang);
    };

    const handleDrawerLogout = () => {
        closeDrawer();
        logout();
    };

    return (
        <>
        <Box
            as="header"
            position="sticky"
            top={0}
            zIndex={100}
            bg="surfaceDark"
            borderBottom="1px solid"
            borderColor="surfaceDarkElevated"
            height="64px"
            display="flex"
            alignItems="center"
        >
            <Container maxW="1200px" w="100%" px={{ base: 4, md: 8 }}>
                <Flex as="nav" align="center" justify="space-between" w="100%">
                    <Link
                        as={RouterLink}
                        to="/"
                        onClick={() => handleNavigation("/")}
                        _hover={{ textDecoration: 'none' }}
                        display={{ base: 'flex', lg: 'none' }}
                        alignItems="center"
                        height="40px"
                    >
                        <HStack spacing="2">
                            <Image src={logoImage} alt="TCF CANADA" height={'32px'} objectFit="contain" />
                            <Text
                                color="onDark"
                                fontSize="md"
                                fontWeight={500}
                                letterSpacing={'-0.3px'}
                                lineHeight={1.2}
                            >Master Key</Text>
                        </HStack>
                    </Link>
                    <HStack as="nav" spacing="6" display={{ base: 'none', lg: 'flex' }}>
                        <Link
                            as={RouterLink}
                            to="/"
                            onClick={() => handleNavigation("/")}
                            _hover={{ textDecoration: 'none' }}
                            display="flex"
                            alignItems="center"
                            height="40px"
                        >
                            <HStack spacing="3">
                                <Image src={logoImage} alt="TCF CANADA" height={'36px'} objectFit="contain" />
                                <Text
                                    ml={2}
                                    color="onDark"
                                    fontSize={"xl"}
                                    fontWeight={500}
                                    letterSpacing={'-0.3px'}
                                    lineHeight={1.2}
                                >Master Key</Text>
                            </HStack>
                        </Link>
                        <Link
                            as={RouterLink}
                            onClick={() => handleNavigation("/")}
                            color="#faf9f5"
                            fontWeight={600}
                            fontSize="sm"
                            _hover={{ bg: 'surfaceDarkSoft', textDecoration: 'none' }}
                            px="3"
                            py="2"
                            borderRadius="md"
                        >
                            {t('header.homepage')}
                        </Link>
                        <Menu isOpen={isObjectiveOpen} onClose={() => setIsObjectiveOpen(false)}>
                            <Box
                                onMouseEnter={() => {
                                    if (objectiveCloseTimer.current) {
                                        clearTimeout(objectiveCloseTimer.current);
                                        objectiveCloseTimer.current = null;
                                    }
                                    setIsObjectiveOpen(true);
                                }}
                                onMouseLeave={() => {
                                    objectiveCloseTimer.current = setTimeout(() => {
                                        setIsObjectiveOpen(false);
                                    }, 150);
                                }}
                            >
                                <MenuButton
                                    as={Button}
                                    variant="ghost"
                                    rightIcon={<AnimatedArrowIcon isOpen={isObjectiveOpen} />}
                                    color="#faf9f5"
                                    fontWeight={600}
                                    fontSize="sm"
                                    px="3"
                                    py="2"
                                    bg="transparent"
                                    _hover={{ bg: "transparent" }}
                                    _active={{ bg: "transparent" }}
                                    _expanded={{ bg: "transparent" }}
                                >
                                    {t('header.objective_testing')}
                                </MenuButton>
                                <MenuList zIndex={1000}>
                                    <MenuItem
                                        as={RouterLink}
                                        onClick={() => handleNavigation("/Comprehension?type=1")}
                                        fontWeight={500}
                                        fontSize="sm"
                                    >
                                        {t('header.listening_comprehension')}
                                </MenuItem>
                                    <MenuItem
                                        as={RouterLink}
                                        onClick={() => handleNavigation("/Comprehension?type=2")}
                                        fontWeight={500}
                                        fontSize="sm"
                                    >
                                        {t('header.reading_comprehension')}
                                    </MenuItem>
                                </MenuList>
                            </Box>
                        </Menu>

                        <Menu isOpen={isSubjectiveOpen} onClose={() => setIsSubjectiveOpen(false)}>
                            <Box
                                onMouseEnter={() => {
                                    if (subjectiveCloseTimer.current) {
                                        clearTimeout(subjectiveCloseTimer.current);
                                        subjectiveCloseTimer.current = null;
                                    }
                                    setIsSubjectiveOpen(true);
                                }}
                                onMouseLeave={() => {
                                    subjectiveCloseTimer.current = setTimeout(() => {
                                        setIsSubjectiveOpen(false);
                                    }, 150);
                                }}
                            >
                                <MenuButton
                                    as={Button}
                                    variant="ghost"
                                    rightIcon={<AnimatedArrowIcon isOpen={isSubjectiveOpen} />}
                                    color="#faf9f5"
                                    fontWeight={600}
                                    fontSize="sm"
                                    px="3"
                                    py="2"
                                    bg="transparent"
                                    _hover={{ bg: "transparent" }}
                                    _active={{ bg: "transparent" }}
                                    _expanded={{ bg: "transparent" }}
                                >
                                    {t('header.subjective_testing')}
                                </MenuButton>
                                <MenuList zIndex={1000}>
                                    <VStack align="start" spacing={0} px="3" py="2">
                                        <Text fontSize="xs" fontWeight={600} color="muted" textTransform="uppercase" letterSpacing="1.5px" mb={1}>
                                            {t('header.speaking')}
                                        </Text>
                                        <MenuItem
                                            as={RouterLink}
                                            onClick={() => handleNavigation("/Subjective?type=2&task=2")}
                                            fontWeight={500}
                                            fontSize="sm"
                                        >
                                            &nbsp;&nbsp;{t('header.task')}&nbsp;2
                                        </MenuItem>
                                        <MenuItem
                                            as={RouterLink}
                                            onClick={() => handleNavigation("/Subjective?type=2&task=3")}
                                            fontWeight={500}
                                            fontSize="sm"
                                        >
                                            &nbsp;&nbsp;{t('header.task')}&nbsp;3
                                        </MenuItem>
                                    </VStack>
                                    <Box borderTop="1px solid" borderColor="hairline" my="1" />
                                    <VStack align="start" spacing={0} px="3" py="2">
                                        <Text fontSize="xs" fontWeight={600} color="muted" textTransform="uppercase" letterSpacing="1.5px" mb={1}>
                                            {t('header.writing')}
                                        </Text>
                                        <MenuItem
                                            as={RouterLink}
                                            onClick={() => handleNavigation("/Subjective?type=1&task=1")}
                                            fontWeight={500}
                                            fontSize="sm"
                                        >
                                            &nbsp;&nbsp;{t('header.task')}&nbsp;1
                                        </MenuItem>
                                        <MenuItem
                                            as={RouterLink}
                                            onClick={() => handleNavigation("/Subjective?type=1&task=2")}
                                            fontWeight={500}
                                            fontSize="sm"
                                        >
                                            &nbsp;&nbsp;{t('header.task')}&nbsp;2
                                        </MenuItem>
                                        <MenuItem
                                            as={RouterLink}
                                            onClick={() => handleNavigation("/Subjective?type=1&task=3")}
                                            fontWeight={500}
                                            fontSize="sm"
                                        >
                                            &nbsp;&nbsp;{t('header.task')}&nbsp;3
                                        </MenuItem>
                                    </VStack>
                                </MenuList>
                            </Box>
                        </Menu>

                        <Link
                            as={RouterLink}
                            onClick={() => handleNavigation("/subscription")}
                            color="#faf9f5"
                            fontWeight={600}
                            fontSize="sm"
                            _hover={{ bg: 'surfaceDarkSoft', textDecoration: 'none' }}
                            px="3"
                            py="2"
                            borderRadius="md"
                        >
                            {t('header.Subscription')}
                        </Link>
                    </HStack>
                    <Flex alignItems="center" gap="3" display={{ base: 'none', lg: 'flex' }}>
                        <Menu>
                            <MenuButton
                                as={Button}
                                variant="outline"
                                borderColor="hairline"
                                bg="canvas"
                                color="body"
                                fontWeight={500}
                                fontSize="sm"
                                h="36px"
                                _hover={{ bg: 'surfaceSoft', borderColor: 'muted' }}
                            >
                                {displayLanguage}
                            </MenuButton>
                            <MenuList zIndex={1000} minW="140px" dir="ltr">
                                {availableLanguages.map((language) => (
                                    <MenuItem
                                        key={language.value}
                                        onClick={() => changeLanguage(language.value)}
                                        fontWeight={500}
                                        fontSize="sm"
                                        bg="transparent"
                                        dir="ltr"
                                        justifyContent="center"
                                        textAlign="center"
                                    >
                                        <Box as="span" dir={language.value === 'ar' ? 'rtl' : 'ltr'}>
                                            {language.label}
                                        </Box>
                                    </MenuItem>
                                ))}
                            </MenuList>
                        </Menu>
                        {isLogin ? (
                            <Menu>
                                <MenuButton
                                    as={Button}
                                    variant="ghost"
                                    p={2}
                                    _hover={{ bg: 'surfaceSoft' }}
                                >
                                    <HStack spacing={2}>
                                        <Avatar
                                            size="sm"
                                            name={cookies.firstName + " " + cookies.lastName}
                                            bg="surfaceCard"
                                            color="ink"
                                        />
                                        <Text fontWeight={500} fontSize="sm" color="#faf9f5">
                                            {cookies.firstName}
                                        </Text>
                                    </HStack>
                                </MenuButton>
                                <MenuList zIndex={1000}>
                                    <MenuItem
                                        as={RouterLink}
                                        onClick={() => handleNavigation("/profile")}
                                        fontWeight={500}
                                        fontSize="sm"
                                        bg="transparent"
                                    >
                                        <Avatar
                                            size="xs"
                                            src={userAvatar}
                                            borderRadius="full"
                                            mr={2}
                                        />
                                        {t('header.my_profile')}
                                    </MenuItem>
                                    <MenuItem
                                        onClick={logout}
                                        fontWeight={500}
                                        fontSize="sm"
                                        bg="transparent"
                                        color="error"
                                    >
                                        <Avatar
                                            size="xs"
                                            src={logoutIcon}
                                            borderRadius="full"
                                            mr={2}
                                        />
                                        {t('login.logout')}
                                    </MenuItem>
                                </MenuList>
                            </Menu>
                        ) : (
                            <Button
                                as={RouterLink}
                                to="/login"
                                variant="primary"
                                size="md"
                                h="40px"
                            >
                                {t('login.login')}
                            </Button>
                        )}
                    </Flex>
                    <IconButton
                        aria-label="Open menu"
                        icon={<HamburgerIcon boxSize={6} />}
                        variant="ghost"
                        color="#faf9f5"
                        display={{ base: 'flex', lg: 'none' }}
                        onClick={openDrawer}
                        _hover={{ bg: 'surfaceDarkSoft' }}
                        _active={{ bg: 'surfaceDarkSoft' }}
                    />
                </Flex>
            </Container>
        </Box>
        <Drawer isOpen={isDrawerOpen} placement={drawerPlacement} onClose={closeDrawer} size="full">
            <DrawerOverlay />
            <DrawerContent bg="surfaceDark" color="onDark">
                <DrawerCloseButton color="onDark" size="lg" />
                <DrawerHeader borderBottomWidth="1px" borderColor="surfaceDarkElevated">
                    <HStack spacing="2">
                        <Image src={logoImage} alt="TCF CANADA" height={'28px'} objectFit="contain" />
                        <Text color="onDark" fontSize="md" fontWeight={500}>Master Key</Text>
                    </HStack>
                </DrawerHeader>
                <DrawerBody>
                    <VStack align="stretch" spacing={2} py={2}>
                        <Button
                            variant="ghost"
                            justifyContent="flex-start"
                            color="onDark"
                            fontWeight={600}
                            onClick={() => handleDrawerNav("/")}
                            _hover={{ bg: 'surfaceDarkSoft' }}
                            _active={{ bg: 'surfaceDarkSoft' }}
                        >
                            {t('header.homepage')}
                        </Button>
                        <Accordion allowToggle>
                            <AccordionItem border="none">
                                <AccordionButton _hover={{ bg: 'surfaceDarkSoft' }} px={4}>
                                    <Box flex="1" textAlign="start" fontWeight={600}>
                                        {t('header.objective_testing')}
                                    </Box>
                                    <AccordionIcon />
                                </AccordionButton>
                                <AccordionPanel pb={2} pl={6}>
                                    <VStack align="stretch" spacing={1}>
                                        <Button
                                            variant="ghost"
                                            justifyContent="flex-start"
                                            color="onDarkSoft"
                                            fontWeight={500}
                                            fontSize="sm"
                                            onClick={() => handleDrawerNav("/Comprehension?type=1")}
                                            _hover={{ bg: 'surfaceDarkSoft' }}
                                        >
                                            {t('header.listening_comprehension')}
                                        </Button>
                                        <Button
                                            variant="ghost"
                                            justifyContent="flex-start"
                                            color="onDarkSoft"
                                            fontWeight={500}
                                            fontSize="sm"
                                            onClick={() => handleDrawerNav("/Comprehension?type=2")}
                                            _hover={{ bg: 'surfaceDarkSoft' }}
                                        >
                                            {t('header.reading_comprehension')}
                                        </Button>
                                    </VStack>
                                </AccordionPanel>
                            </AccordionItem>
                            <AccordionItem border="none">
                                <AccordionButton _hover={{ bg: 'surfaceDarkSoft' }} px={4}>
                                    <Box flex="1" textAlign="start" fontWeight={600}>
                                        {t('header.subjective_testing')}
                                    </Box>
                                    <AccordionIcon />
                                </AccordionButton>
                                <AccordionPanel pb={2} pl={6}>
                                    <VStack align="stretch" spacing={1}>
                                        <Text fontSize="xs" fontWeight={600} color="onDarkSoft" textTransform="uppercase" letterSpacing="1.5px" mt={2} mb={1}>
                                            {t('header.speaking')}
                                        </Text>
                                        <Button variant="ghost" justifyContent="flex-start" color="onDarkSoft" fontWeight={500} fontSize="sm" onClick={() => handleDrawerNav("/Subjective?type=2&task=2")} _hover={{ bg: 'surfaceDarkSoft' }}>
                                            {t('header.task')}&nbsp;2
                                        </Button>
                                        <Button variant="ghost" justifyContent="flex-start" color="onDarkSoft" fontWeight={500} fontSize="sm" onClick={() => handleDrawerNav("/Subjective?type=2&task=3")} _hover={{ bg: 'surfaceDarkSoft' }}>
                                            {t('header.task')}&nbsp;3
                                        </Button>
                                        <Text fontSize="xs" fontWeight={600} color="onDarkSoft" textTransform="uppercase" letterSpacing="1.5px" mt={2} mb={1}>
                                            {t('header.writing')}
                                        </Text>
                                        <Button variant="ghost" justifyContent="flex-start" color="onDarkSoft" fontWeight={500} fontSize="sm" onClick={() => handleDrawerNav("/Subjective?type=1&task=1")} _hover={{ bg: 'surfaceDarkSoft' }}>
                                            {t('header.task')}&nbsp;1
                                        </Button>
                                        <Button variant="ghost" justifyContent="flex-start" color="onDarkSoft" fontWeight={500} fontSize="sm" onClick={() => handleDrawerNav("/Subjective?type=1&task=2")} _hover={{ bg: 'surfaceDarkSoft' }}>
                                            {t('header.task')}&nbsp;2
                                        </Button>
                                        <Button variant="ghost" justifyContent="flex-start" color="onDarkSoft" fontWeight={500} fontSize="sm" onClick={() => handleDrawerNav("/Subjective?type=1&task=3")} _hover={{ bg: 'surfaceDarkSoft' }}>
                                            {t('header.task')}&nbsp;3
                                        </Button>
                                    </VStack>
                                </AccordionPanel>
                            </AccordionItem>
                        </Accordion>
                        <Button
                            variant="ghost"
                            justifyContent="flex-start"
                            color="onDark"
                            fontWeight={600}
                            onClick={() => handleDrawerNav("/subscription")}
                            _hover={{ bg: 'surfaceDarkSoft' }}
                        >
                            {t('header.Subscription')}
                        </Button>
                        <Divider borderColor="surfaceDarkElevated" my={2} />
                        <Accordion allowToggle>
                            <AccordionItem border="none">
                                <AccordionButton _hover={{ bg: 'surfaceDarkSoft' }} px={4}>
                                    <Box flex="1" textAlign="start" fontWeight={600}>
                                        {displayLanguage}
                                    </Box>
                                    <AccordionIcon />
                                </AccordionButton>
                                <AccordionPanel pb={2} pl={6}>
                                    <VStack align="stretch" spacing={1}>
                                        {availableLanguages.map((language) => (
                                            <Button
                                                key={language.value}
                                                variant="ghost"
                                                justifyContent="flex-start"
                                                color="onDarkSoft"
                                                fontWeight={500}
                                                fontSize="sm"
                                                onClick={() => handleDrawerLanguage(language.value)}
                                                _hover={{ bg: 'surfaceDarkSoft' }}
                                                dir="ltr"
                                            >
                                                <Box as="span" dir={language.value === 'ar' ? 'rtl' : 'ltr'}>
                                                    {language.label}
                                                </Box>
                                            </Button>
                                        ))}
                                    </VStack>
                                </AccordionPanel>
                            </AccordionItem>
                        </Accordion>
                        <Divider borderColor="surfaceDarkElevated" my={2} />
                        {isLogin ? (
                            <>
                                <Button
                                    variant="ghost"
                                    justifyContent="flex-start"
                                    color="onDark"
                                    fontWeight={600}
                                    onClick={() => handleDrawerNav("/profile")}
                                    _hover={{ bg: 'surfaceDarkSoft' }}
                                    leftIcon={<Avatar size="xs" src={userAvatar} borderRadius="full" />}
                                >
                                    {t('header.my_profile')}
                                </Button>
                                <Button
                                    variant="ghost"
                                    justifyContent="flex-start"
                                    color="error"
                                    fontWeight={600}
                                    onClick={handleDrawerLogout}
                                    _hover={{ bg: 'surfaceDarkSoft' }}
                                    leftIcon={<Avatar size="xs" src={logoutIcon} borderRadius="full" />}
                                >
                                    {t('login.logout')}
                                </Button>
                            </>
                        ) : (
                            <Button
                                as={RouterLink}
                                to="/login"
                                onClick={closeDrawer}
                                variant="primary"
                                size="lg"
                                w="100%"
                            >
                                {t('login.login')}
                            </Button>
                        )}
                    </VStack>
                </DrawerBody>
            </DrawerContent>
        </Drawer>
        </>
    )
}

export default Header;
